import { Badge, Card, Group, Stack, Text, Avatar, Tooltip } from '@mantine/core'
import {
  IconCalendarEvent,
  IconChecklist,
  IconMessageCircle2,
  IconPaperclip,
  IconBrandTelegram,
  IconArrowUpRight,
} from '@tabler/icons-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { TaskCardDto } from '@ledokol/contracts'
import dayjs from 'dayjs'

const PRIORITY: Record<string, { color: string; label: string }> = {
  low: { color: 'gray', label: 'Низкий' },
  med: { color: 'blue', label: 'Средний' },
  high: { color: 'orange', label: 'Высокий' },
  urgent: { color: 'red', label: 'Срочный' },
}

const DUE_COLOR: Record<string, string> = {
  none: 'gray',
  ok: 'green',
  soon: 'yellow',
  overdue: 'red',
}

export function TaskCard({
  task,
  onClick,
}: {
  task: TaskCardDto
  onClick?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: 'task', task },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      withBorder
      p="sm"
      radius="md"
      shadow="xs"
      onClick={(e) => {
        // Не открываем модалку при drag
        if (!isDragging) {
          e.stopPropagation()
          onClick?.()
        }
      }}
      styles={{ root: { cursor: 'grab' } }}
    >
      <Stack gap={6}>
        <Group gap={6}>
          {task.tags.slice(0, 3).map((t) => (
            <Badge key={t.id} variant="dot" color={hexToMantine(t.color)} size="xs">
              {t.name}
            </Badge>
          ))}
          {task.source === 'telegram' && (
            <Tooltip label="Создано из Telegram">
              <IconBrandTelegram size={14} color="var(--mantine-color-blue-6)" />
            </Tooltip>
          )}
          {task.type === 'report' && (
            <Tooltip label="Регулярный отчёт">
              <Badge size="xs" variant="light" color="violet">
                Отчёт
              </Badge>
            </Tooltip>
          )}
        </Group>

        <Text size="sm" fw={500} lineClamp={3}>
          <Text span c="dimmed" fw={500} mr={4}>#{task.human_id}</Text>
          {task.title}
        </Text>

        <Group justify="space-between" align="center" gap="xs" mt={4}>
          <Group gap={6}>
            {task.due_at && (
              <Badge color={DUE_COLOR[task.due_state]} variant="light" size="xs" leftSection={<IconCalendarEvent size={10} />}>
                {dayjs(task.due_at).format('D MMM')}
              </Badge>
            )}
            <Badge color={PRIORITY[task.priority]?.color} variant="dot" size="xs">
              {PRIORITY[task.priority]?.label}
            </Badge>
          </Group>

          {task.assignee && (
            <Tooltip label={task.assignee.full_name}>
              <Avatar size={22} radius="xl" color="blue">
                {task.assignee.full_name[0]}
              </Avatar>
            </Tooltip>
          )}
        </Group>

        {(task.comments_count > 0 || task.attachments_count > 0 || task.checklist) && (
          <Group gap="md" mt={4} c="dimmed">
            {task.comments_count > 0 && (
              <Group gap={3}>
                <IconMessageCircle2 size={13} />
                <Text size="xs">{task.comments_count}</Text>
              </Group>
            )}
            {task.attachments_count > 0 && (
              <Group gap={3}>
                <IconPaperclip size={13} />
                <Text size="xs">{task.attachments_count}</Text>
              </Group>
            )}
            {task.checklist && (
              <Group gap={3}>
                <IconChecklist size={13} />
                <Text size="xs">
                  {task.checklist.done}/{task.checklist.total}
                </Text>
              </Group>
            )}
            <span style={{ flex: 1 }} />
            <IconArrowUpRight size={13} opacity={0.3} />
          </Group>
        )}
      </Stack>
    </Card>
  )
}

// Mantine не принимает hex — мапим в ближайший палитру
function hexToMantine(hex: string): string {
  const map: Record<string, string> = {
    '#6366f1': 'indigo', '#ef4444': 'red', '#22c55e': 'green',
    '#f59e0b': 'orange', '#94a3b8': 'gray', '#3b82f6': 'blue',
    '#a855f7': 'violet',
  }
  return map[hex.toLowerCase()] ?? 'blue'
}
